import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider } from '@/contexts/AuthContext';
import { HelmetProvider } from 'react-helmet-async';

// Lazy-loaded pages for code splitting
const Explore = React.lazy(() => import('@/pages/Explore'));
const PaperDetails = React.lazy(() => import('@/pages/PaperDetails'));
const AdminEmails = React.lazy(() => import('@/pages/admin/AdminEmails'));

// Eagerly loaded pages (critical path)
import Index from '@/pages/Index';
import About from '@/pages/About';
import Upload from '@/pages/Upload';
import UploadSuccess from '@/pages/UploadSuccess';
import Analytics from '@/pages/Analytics';
import Auth from '@/pages/Auth';
import AuthorProfilePage from '@/pages/author/AuthorProfilePage';
import Profile from '@/pages/Profile';
import Settings from '@/pages/Settings';
import ActivityDashboardV2 from '@/pages/ActivityDashboardV2';
import NotFound from '@/pages/NotFound';
import PaperPdfRedirect from '@/pages/PaperPdfRedirect';
import InvitationLanding from '@/pages/InvitationLanding';
import UnsubscribeReviewInvitations from '@/pages/UnsubscribeReviewInvitations';
import UnsubscribeNotifications from '@/pages/UnsubscribeNotifications';

// Legal pages
import PrivacyPolicy from '@/pages/PrivacyPolicy';
import TermsOfService from '@/pages/TermsOfService';
import CancelContract from '@/pages/CancelContract';
import CookiePolicy from '@/pages/CookiePolicy';
import CancellationPolicy from '@/pages/CancellationPolicy';
import RefundPolicy from '@/pages/RefundPolicy';
import Copyright from '@/pages/Copyright';
import Imprint from '@/pages/Imprint';

// Other pages
import FAQ from '@/pages/FAQ';
import Guidelines from '@/pages/Guidelines';
import AITransparency from '@/pages/AITransparency';
import EUProcessing from '@/pages/EUProcessing';
import Accessibility from '@/pages/Accessibility';

import PricingV2 from '@/pages/PricingV2';
import PaperVerseVsOverleaf from '@/pages/compare/PaperVerseVsOverleaf';

// Protected routes
import ProtectedRoute from '@/components/auth/ProtectedRoute';

// Admin pages
import AdminContent from '@/pages/AdminContent';
import SearchIndexing from '@/pages/admin/SearchIndexing';
import LegalDocuments from '@/pages/admin/LegalDocuments';
import InstitutionalDashboard from '@/pages/admin/InstitutionalDashboard';
import WriterTelemetry from '@/pages/admin/WriterTelemetry';

// Development pages
import StorageDiagnostics from '@/pages/dev/StorageDiagnostics';
import StorageDocumentation from '@/pages/dev/StorageDocumentation';

// Writer (admin-only beta — see .lovable/plan.md)
import WriterHome from '@/pages/writer/WriterHome';
import WriterProject from '@/pages/writer/WriterProject';

// Components
import CookieBanner from '@/components/common/CookieBanner';
import PolicyUpdateBanner from '@/components/legal/PolicyUpdateBanner';
import ScrollToTop from '@/components/common/ScrollToTop';
import PageTracker from '@/components/analytics/PageTracker';
import GlobalClickTracker from '@/components/analytics/GlobalClickTracker';

// Skeleton for lazy-loaded pages
import ExploreSkeleton from '@/components/explore/ExploreSkeleton';
import PaperDetailsSkeleton from '@/components/papers/PaperDetailsSkeleton';

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
          <Router>
            <ScrollToTop />
            <PageTracker />
            <GlobalClickTracker />
            <div className="min-h-screen bg-background text-foreground">
              <Routes>
                <Route path="/" element={<Index />} />
                <Route path="/about" element={<About />} />
                <Route path="/explore" element={
                  <Suspense fallback={<ExploreSkeleton />}>
                    <Explore />
                  </Suspense>
                } />
                <Route path="/paper/:id" element={
                  <Suspense fallback={<PaperDetailsSkeleton />}>
                    <PaperDetails />
                  </Suspense>
                } />
                <Route path="/paper/:id/pdf" element={<PaperPdfRedirect />} />
                <Route path="/paper/:id/v/:versionId/pdf" element={<PaperPdfRedirect />} />
                <Route path="/author/:id" element={<AuthorProfilePage />} />
                <Route path="/auth" element={<Auth />} />
                <Route path="/pricing" element={<PricingV2 />} />
                <Route path="/faq" element={<FAQ />} />
                <Route path="/compare/paperverse-vs-overleaf" element={<PaperVerseVsOverleaf />} />
                <Route path="/documentation" element={<Navigate to="/faq" replace />} />
                <Route path="/guidelines" element={<Guidelines />} />
                
                {/* Legal pages */}
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/terms-of-service" element={<TermsOfService />} />
                <Route path="/cookie-policy" element={<CookiePolicy />} />
                <Route path="/cancellation-policy" element={<CancellationPolicy />} />
                <Route path="/cancel-contract" element={<CancelContract />} />
                <Route path="/refund-policy" element={<RefundPolicy />} />
                <Route path="/copyright" element={<Copyright />} />
                <Route path="/imprint" element={<Imprint />} />
                <Route path="/ai-transparency" element={<AITransparency />} />
                <Route path="/eu-processing" element={<EUProcessing />} />
                {/* Sub-processor list folded into the Privacy Policy; keep old links alive. */}
                <Route path="/subprocessors" element={<Navigate to="/privacy-policy#sub-processors" replace />} />
                <Route path="/accessibility" element={<Accessibility />} />
                
                {/* Protected routes */}
                <Route path="/upload" element={<Upload />} />
                <Route path="/upload/success/:paperId" element={
                  <ProtectedRoute>
                    <UploadSuccess />
                  </ProtectedRoute>
                } />
                <Route path="/profile" element={
                  <ProtectedRoute>
                    <Profile />
                  </ProtectedRoute>
                } />
                <Route path="/settings" element={
                  <ProtectedRoute>
                    <Settings />
                  </ProtectedRoute>
                } />
                <Route path="/dashboard" element={
                  <ProtectedRoute>
                    <ActivityDashboardV2 />
                  </ProtectedRoute>
                } />
                {/* Activity v2 is now the default dashboard; keep the old link alive. */}
                <Route path="/dashboard-v2" element={<Navigate to="/dashboard" replace />} />
                
                {/* Admin routes */}
                <Route path="/admin/analytics" element={
                  <ProtectedRoute>
                    <Analytics />
                  </ProtectedRoute>
                } />
                <Route path="/admin/content" element={
                  <ProtectedRoute>
                    <AdminContent />
                  </ProtectedRoute>
                } />
                <Route path="/admin/search-indexing" element={
                  <ProtectedRoute>
                    <SearchIndexing />
                  </ProtectedRoute>
                } />
                <Route path="/admin/legal" element={
                  <ProtectedRoute>
                    <LegalDocuments />
                  </ProtectedRoute>
                } />
                <Route path="/admin/institutional" element={
                  <ProtectedRoute>
                    <InstitutionalDashboard />
                  </ProtectedRoute>
                } />
                <Route path="/admin/emails" element={
                  <ProtectedRoute>
                    <Suspense fallback={null}>
                      <AdminEmails />
                    </Suspense>
                  </ProtectedRoute>
                } />
                <Route path="/admin/writer-telemetry" element={
                  <ProtectedRoute>
                    <WriterTelemetry />
                  </ProtectedRoute>
                } />

                {/* Development Tools Routes */}
                <Route path="/dev/storage-diagnostics" element={
                  <ProtectedRoute>
                    <StorageDiagnostics />
                  </ProtectedRoute>
                } />
                <Route path="/dev/storage-documentation" element={
                  <ProtectedRoute>
                    <StorageDocumentation />
                  </ProtectedRoute>
                } />

                {/* Writer: /write is public (guests get the prompt-first
                    landing); individual projects stay protected. */}
                <Route path="/write" element={<WriterHome />} />
                <Route path="/write/:projectId" element={
                  <ProtectedRoute>
                    <WriterProject />
                  </ProtectedRoute>
                } />

                {/* Pricing v2 is now the public /pricing page; keep the old link alive. */}
                <Route path="/pricing-v2" element={<Navigate to="/pricing" replace />} />


                {/* Catch-all route for 404 pages - exclude static files */}
                <Route path="/invitations/:token" element={<InvitationLanding />} />
                <Route path="/unsubscribe/review-invitations" element={<UnsubscribeReviewInvitations />} />
                <Route path="/unsubscribe/notifications" element={<UnsubscribeNotifications />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
              
              <CookieBanner />
              <PolicyUpdateBanner />
              <Toaster 
                position="top-center" 
                toastOptions={{
                  duration: 4000,
                  style: {
                    background: 'hsl(var(--background))',
                    color: 'hsl(var(--foreground))',
                    border: '1px solid hsl(var(--border))',
                  },
                }}
              />
            </div>
          </Router>
        </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
